import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../services/api';
import ConfirmationModal from '../components/ConfirmationModal';
import ShipmentModal from '../components/ShipmentModal';
import InternalNotesModal from '../components/InternalNotesModal';
import PackingGuideDrawer from '../components/PackingGuideDrawer';
import GroupCard from '../components/GroupCard';
import { EyeIcon, PencilIcon, CubeIcon } from '@heroicons/react/24/outline';
import PageHeader from '../components/ui/PageHeader';
import useGroupedBoard from '../hooks/useGroupedBoard';
import { QUOTE_STATUS_CARD_COLORS } from '../constants/quoteStatusColors';

const PACKING_GUIDE_STATUSES = ['ONGOING', 'INSPECTION', 'READY'];

const BOARD_STATUSES = [
  'PAID',
  'ONGOING',
  'INSPECTION',
  'READY',
  'DELIVERING',
  'DONE',
  'ON_HOLD',
  'CANCELLED',
];
const BOARD_STATUSES_CSV = BOARD_STATUSES.join(',');
const LEADS_STATUSES_CSV = 'CREATED,BUILT,SLICED,SUBMITTED,PENDING_PAYMENT';

// Status color mapping (imported from the shared constants module so SlicerQuotes
// and GroupCard stay in sync when a new status is added). Includes a CANCELLED
// entry so that column header no longer falls through to the grey DEFAULT.
const STATUS_COLORS = QUOTE_STATUS_CARD_COLORS;

// Tab configuration
const TABS = {
  BOARD: 'board',
  LEADS: 'leads'
};

// Convert status key to readable label
const formatStatusLabel = (status) => {
  return status
    .split('_')
    .map(word => word.charAt(0) + word.slice(1).toLowerCase())
    .join(' ');
};

function SlicerQuotes() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(TABS.BOARD);

  // Leads-tab state (kept entirely separate from Board state)
  const [leadsBoard, setLeadsBoard] = useState({});
  const [leadsAvailableStatuses, setLeadsAvailableStatuses] = useState([]);
  const [leadsCounts, setLeadsCounts] = useState({});
  const [leadsLoading, setLeadsLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);

  // Drag + modal state (shared by both tabs but evolves to group shape on Board)
  const [draggedGroup, setDraggedGroup] = useState(null);
  const [draggedQuote, setDraggedQuote] = useState(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showShipmentModal, setShowShipmentModal] = useState(false);
  const [pendingStatusChange, setPendingStatusChange] = useState(null);
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [packingGuideQuoteId, setPackingGuideQuoteId] = useState(null);

  // Filters (lazy-init from URL once)
  const [filters, setFilters] = useState(() => ({
    contactEmail: searchParams.get('contactEmail') || '',
    linkedOrderId: searchParams.get('linkedOrderId') || '',
    step: searchParams.get('step') || '',
  }));

  // Sync state → URL (one direction only). `step` is a Leads-tab-only filter,
  // so we only surface it in the URL when the Leads tab is active — otherwise
  // switching Leads → Board and sharing the URL would leak a hidden step filter.
  useEffect(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        const upsert = (key, val) => {
          if (val) next.set(key, val);
          else next.delete(key);
        };
        upsert('contactEmail', filters.contactEmail);
        upsert('linkedOrderId', filters.linkedOrderId);
        upsert('step', activeTab === TABS.LEADS ? filters.step : '');
        return next;
      },
      { replace: true }
    );
  }, [filters, activeTab, setSearchParams]);

  // ─── Board tab (grouped) ────────────────────────────────────────────────────
  const {
    groups,
    columns,
    loading: boardLoading,
    refetch: refetchBoard,
    quotesInColumn,
  } = useGroupedBoard({
    contactEmail: filters.contactEmail || undefined,
    linkedOrderId: filters.linkedOrderId || undefined,
    statuses: BOARD_STATUSES_CSV,
    enabled: activeTab === TABS.BOARD,
  });

  // ─── Leads tab (flat + paginated) ───────────────────────────────────────────
  const buildLeadsParams = useCallback(() => {
    const params = { statuses: LEADS_STATUSES_CSV };
    if (filters.contactEmail) params.contactEmail = filters.contactEmail;
    if (filters.step) params.step = filters.step;
    return params;
  }, [filters]);

  const fetchLeadsBoard = useCallback(async () => {
    try {
      setLeadsLoading(true);
      const params = { ...buildLeadsParams(), page: 1, limit: 20 };
      const response = await api.get('/auth/slicer-quote/board', { params });
      const data = response.data.data || response.data;
      setLeadsAvailableStatuses(data.availableStatuses || []);
      setLeadsBoard(data.board || {});
      setLeadsCounts(data.counts || {});
      setPage(1);
    } catch (error) {
      console.error('Error fetching leads board:', error);
      alert('Failed to load slicer quotes');
    } finally {
      setLeadsLoading(false);
    }
  }, [buildLeadsParams]);

  const hasMoreLeads = leadsAvailableStatuses.some(
    (status) => (leadsBoard[status]?.length || 0) < (leadsCounts[status] || 0)
  );
  const hasMoreLeadsRef = useRef(hasMoreLeads);
  hasMoreLeadsRef.current = hasMoreLeads;

  const fetchNextLeadsPage = useCallback(async () => {
    if (loadingMore || !hasMoreLeadsRef.current) return;
    const nextPage = page + 1;
    try {
      setLoadingMore(true);
      const params = { ...buildLeadsParams(), page: nextPage, limit: 20 };
      const response = await api.get('/auth/slicer-quote/board', { params });
      const data = response.data.data || response.data;
      const newBoard = data.board || {};
      if (Object.keys(newBoard).length === 0) return;
      setLeadsBoard((prev) => {
        const merged = { ...prev };
        for (const status of Object.keys(newBoard)) {
          const existingIds = new Set((merged[status] || []).map((q) => q._id));
          const uniqueNew = newBoard[status].filter((q) => !existingIds.has(q._id));
          merged[status] = [...(merged[status] || []), ...uniqueNew];
        }
        return merged;
      });
      setLeadsCounts(data.counts || {});
      setPage(nextPage);
    } catch (error) {
      console.error('Error fetching next page:', error);
    } finally {
      setLoadingMore(false);
    }
  }, [buildLeadsParams, loadingMore, page]);

  const observerRef = useRef(null);
  const scrollSentinelRef = useCallback(
    (node) => {
      if (observerRef.current) observerRef.current.disconnect();
      if (node) {
        observerRef.current = new IntersectionObserver(
          (entries) => {
            if (entries[0].isIntersecting) fetchNextLeadsPage();
          },
          { rootMargin: '0px 0px 300px 0px' }
        );
        observerRef.current.observe(node);
      }
    },
    [fetchNextLeadsPage]
  );

  useEffect(() => {
    if (activeTab === TABS.LEADS) fetchLeadsBoard();
  }, [activeTab, fetchLeadsBoard]);

  // ─── Drag handlers ──────────────────────────────────────────────────────────

  // Board (group-aware)
  const handleGroupDragStart = (e, group, fromStatus, subsetIds) => {
    setDraggedGroup({ group, fromStatus, subsetIds });
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', group.groupKey);
  };

  // Leads (per-quote)
  const handleQuoteDragStart = (e, quote, fromStatus) => {
    setDraggedQuote({ quote, fromStatus });
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  // Backend state machine only allows DELIVERING from READY or ON_HOLD.
  // Guard here so a drop from e.g. ONGOING fails fast with a clear message
  // instead of opening the shipment modal and eating a generic 400 on submit.
  const SHIPPABLE_SOURCE_STATUSES = new Set(['READY', 'ON_HOLD']);

  const handleBoardDrop = (e, toStatus) => {
    e.preventDefault();
    if (!draggedGroup || draggedGroup.fromStatus === toStatus) {
      setDraggedGroup(null);
      return;
    }
    if (toStatus === 'DELIVERING' && !SHIPPABLE_SOURCE_STATUSES.has(draggedGroup.fromStatus)) {
      setDraggedGroup(null);
      alert(
        `Cannot ship from ${draggedGroup.fromStatus}. Move to READY (or ON_HOLD) first.`
      );
      return;
    }
    setPendingStatusChange({
      group: draggedGroup.group,
      fromStatus: draggedGroup.fromStatus,
      toStatus,
      quoteIds: draggedGroup.subsetIds,
    });
    setDraggedGroup(null);
    if (toStatus === 'DELIVERING') setShowShipmentModal(true);
    else setShowConfirmation(true);
  };

  const handleLeadsDrop = (e, toStatus) => {
    e.preventDefault();
    if (!draggedQuote || draggedQuote.fromStatus === toStatus) {
      setDraggedQuote(null);
      return;
    }
    if (toStatus === 'DELIVERING' && !SHIPPABLE_SOURCE_STATUSES.has(draggedQuote.fromStatus)) {
      setDraggedQuote(null);
      alert(
        `Cannot ship from ${draggedQuote.fromStatus}. Move to READY (or ON_HOLD) first.`
      );
      return;
    }
    setPendingStatusChange({
      quote: draggedQuote.quote,
      fromStatus: draggedQuote.fromStatus,
      toStatus,
    });
    setDraggedQuote(null);
    if (toStatus === 'DELIVERING') setShowShipmentModal(true);
    else setShowConfirmation(true);
  };

  // ─── Mutations ──────────────────────────────────────────────────────────────

  const refetchActive = useCallback(async () => {
    if (activeTab === TABS.BOARD) await refetchBoard();
    else await fetchLeadsBoard();
  }, [activeTab, refetchBoard, fetchLeadsBoard]);

  const confirmStatusChange = async () => {
    if (!pendingStatusChange) return;
    try {
      if (pendingStatusChange.group) {
        const { group, toStatus, quoteIds } = pendingStatusChange;
        await api.patch(
          `/auth/slicer-quote/groups/${encodeURIComponent(group.groupKey)}/transition`,
          { targetStatus: toStatus, quoteIds }
        );
      } else {
        const { quote, toStatus } = pendingStatusChange;
        await api.patch(`/auth/slicer-quote/${quote._id}`, { status: toStatus });
      }
      setShowConfirmation(false);
      setPendingStatusChange(null);
      await refetchActive();
    } catch (error) {
      console.error('Error updating status:', error);
      const msg =
        error.response?.data?.error || error.response?.data?.message || error.message;
      alert('Failed to update status: ' + msg);
    }
  };

  const cancelStatusChange = () => {
    setShowConfirmation(false);
    setPendingStatusChange(null);
  };

  const confirmShipmentChange = async (payload) => {
    if (!pendingStatusChange) return;
    try {
      if (pendingStatusChange.group) {
        const { group } = pendingStatusChange;
        const { quoteIds, shipments, internalNotes } = payload;
        await api.patch(
          `/auth/slicer-quote/groups/${encodeURIComponent(group.groupKey)}/deliver`,
          { quoteIds, shipments, ...(internalNotes ? { internalNotes } : {}) }
        );
      } else {
        const { quote } = pendingStatusChange;
        const { shipments, internalNotes } = payload;
        await api.patch(`/auth/slicer-quote/${quote._id}`, {
          status: 'DELIVERING',
          shipments,
          ...(internalNotes ? { internalNotes } : {}),
        });
      }
      setShowShipmentModal(false);
      setPendingStatusChange(null);
      await refetchActive();
    } catch (error) {
      console.error('Error shipping:', error);
      const msg =
        error.response?.data?.error || error.response?.data?.message || error.message;
      alert('Failed to ship: ' + msg);
    }
  };

  const handleEditNotes = (quote) => {
    setSelectedQuote(quote);
    setShowNotesModal(true);
  };

  const handleSaveNotes = async (notes) => {
    try {
      await api.patch(`/auth/slicer-quote/${selectedQuote._id}`, {
        internalNotes: notes,
      });
      setShowNotesModal(false);
      setSelectedQuote(null);
      await refetchActive();
    } catch (error) {
      console.error('Error saving notes:', error);
      alert('Failed to save internal notes');
    }
  };

  const handleViewDetails = (quoteId) => {
    navigate(`/slicer-quotes/${quoteId}`);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // ─── Render helpers ─────────────────────────────────────────────────────────

  const isBoard = activeTab === TABS.BOARD;
  const loading = isBoard ? boardLoading : leadsLoading;
  const renderedStatuses = isBoard ? BOARD_STATUSES : leadsAvailableStatuses;

  const boardColumnCounts = useMemo(() => {
    const counts = {};
    if (!isBoard) return counts;
    for (const status of BOARD_STATUSES) {
      const groupKeys = columns[status] || [];
      const quoteCount = groupKeys.reduce(
        (sum, k) => sum + quotesInColumn(k, status).length,
        0
      );
      counts[status] = { groups: groupKeys.length, quotes: quoteCount };
    }
    return counts;
  }, [isBoard, columns, quotesInColumn]);

  const renderColumn = (statusKey) => {
    const statusColor = STATUS_COLORS[statusKey] || STATUS_COLORS.DEFAULT;
    const statusLabel = formatStatusLabel(statusKey);
    const onDrop = isBoard
      ? (e) => handleBoardDrop(e, statusKey)
      : (e) => handleLeadsDrop(e, statusKey);

    let countBadge;
    if (isBoard) {
      const c = boardColumnCounts[statusKey] || { groups: 0, quotes: 0 };
      countBadge = (
        <span
          className="bg-white px-2 py-1 rounded-full text-sm font-medium"
          title={`${c.groups} group(s), ${c.quotes} quote(s)`}
        >
          {c.quotes}
        </span>
      );
    } else {
      countBadge = (
        <span className="bg-white px-2 py-1 rounded-full text-sm font-medium">
          {leadsCounts[statusKey] || 0}
        </span>
      );
    }

    return (
      <div
        key={statusKey}
        className="flex-shrink-0 w-80"
        onDragOver={handleDragOver}
        onDrop={onDrop}
      >
        <div className={`${statusColor} border-2 rounded-t-lg px-4 py-3`}>
          <div className="flex items-center justify-between">
            <h2 className="font-semibold text-lg">{statusLabel}</h2>
            {countBadge}
          </div>
        </div>

        <div className="bg-gray-50 border-2 border-t-0 rounded-b-lg min-h-96 p-3 space-y-3">
          {isBoard ? renderBoardColumnBody(statusKey) : renderLeadsColumnBody(statusKey)}
        </div>
      </div>
    );
  };

  const renderBoardColumnBody = (statusKey) => {
    const groupKeys = columns[statusKey] || [];
    if (groupKeys.length === 0) {
      return (
        <div className="text-center text-gray-400 py-8">
          <div className="text-sm">No quotes in this status</div>
          <div className="text-xs mt-1">Drag quotes here to change status</div>
        </div>
      );
    }
    return groupKeys.map((groupKey) => {
      const group = groups[groupKey];
      const subset = quotesInColumn(groupKey, statusKey);
      if (!group || subset.length === 0) return null;
      const compact =
        (group.aggregate?.total ?? subset.length) === 1 && !group.linkedOrderNumber;
      return (
        <GroupCard
          key={groupKey}
          group={group}
          status={statusKey}
          subsetQuotes={subset}
          compact={compact}
          draggable={!boardLoading}
          onDragStart={handleGroupDragStart}
          onViewDetails={handleViewDetails}
          onEditNotes={handleEditNotes}
          onOpenPacking={(id) => setPackingGuideQuoteId(id)}
        />
      );
    });
  };

  const renderLeadsColumnBody = (statusKey) => {
    const quotes = leadsBoard[statusKey];
    if (!quotes || quotes.length === 0) {
      return (
        <div className="text-center text-gray-400 py-8">
          <div className="text-sm">No quotes in this status</div>
          <div className="text-xs mt-1">Drag quotes here to change status</div>
        </div>
      );
    }
    return quotes.map((quote) => (
      <div
        key={quote._id}
        draggable
        onDragStart={(e) => handleQuoteDragStart(e, quote, statusKey)}
        className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 cursor-move hover:shadow-md transition-shadow"
      >
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1">
            <div className="font-semibold text-gray-900 mb-1">{quote.contact?.name}</div>
            <div className="text-sm text-gray-600 mb-1">{quote.contact?.email}</div>
            <div className="text-xs text-gray-500">{formatDate(quote.createdAt)}</div>
          </div>
          <div className="flex flex-col items-end gap-1">
            <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
              Step {quote.step}
            </span>
          </div>
        </div>

        <div className="space-y-2 mb-3 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-600">Quantity:</span>
            <span className="font-medium">{quote.quantity}</span>
          </div>
          {quote.priceSnapshot && (
            <div className="flex justify-between">
              <span className="text-gray-600">Total:</span>
              <span className="font-medium text-green-600 text-right">
                {(quote.priceSnapshot.totalPriceInCents / 100).toFixed(2)}{' '}
                {quote.priceSnapshot.currency || 'EUR'}
                {quote.quantity > 1 && (
                  <>
                    <br />
                    <small>
                      ({(quote.priceSnapshot.unitPriceInCents / 100).toFixed(2)} / unit)
                    </small>
                  </>
                )}
              </span>
            </div>
          )}
        </div>

        {quote.internalNotes && (
          <div className="mb-3 p-2 bg-yellow-50 border border-yellow-200 rounded text-xs">
            <div className="font-medium text-yellow-800 mb-1">Internal Notes:</div>
            <div className="text-yellow-700 line-clamp-2">{quote.internalNotes}</div>
          </div>
        )}

        <div className="flex gap-2 pt-3 border-t border-gray-100">
          <button
            onClick={() => handleViewDetails(quote._id)}
            className="flex-1 flex items-center justify-center gap-1 px-3 py-2 text-sm bg-blue-50 text-blue-700 rounded hover:bg-blue-100 transition-colors"
            title="View Details"
          >
            <EyeIcon className="h-4 w-4" />
            Details
          </button>
          <button
            onClick={() => handleEditNotes(quote)}
            className="flex-1 flex items-center justify-center gap-1 px-3 py-2 text-sm bg-gray-50 text-gray-700 rounded hover:bg-gray-100 transition-colors"
            title="Edit Internal Notes"
          >
            <PencilIcon className="h-4 w-4" />
            Notes
          </button>
          {PACKING_GUIDE_STATUSES.includes(statusKey) && (
            <button
              onClick={() => setPackingGuideQuoteId(quote._id)}
              className="flex-1 flex items-center justify-center gap-1 px-3 py-2 text-sm bg-indigo-50 text-indigo-700 rounded hover:bg-indigo-100 transition-colors"
              title="Packing Guide"
            >
              <CubeIcon className="h-4 w-4" />
              Pack
            </button>
          )}
        </div>
      </div>
    ));
  };

  if (loading) {
    return <div className="p-6">Loading...</div>;
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <PageHeader title="Slicer Quotes Board" />

        {/* Tabs */}
        <div className="border-b border-gray-200 mb-4">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab(TABS.BOARD)}
              className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === TABS.BOARD
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Board
            </button>
            <button
              onClick={() => setActiveTab(TABS.LEADS)}
              className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === TABS.LEADS
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Leads
            </button>
          </nav>
        </div>

        {/* Filters */}
        <div className="flex gap-4 mb-4">
          <div className="flex-1">
            <input
              type="text"
              placeholder="Filter by email..."
              value={filters.contactEmail}
              onChange={(e) => setFilters({ ...filters, contactEmail: e.target.value })}
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          {isBoard && (
            <div className="flex-1">
              <input
                type="text"
                placeholder="Filter by order ID..."
                value={filters.linkedOrderId}
                onChange={(e) =>
                  setFilters({ ...filters, linkedOrderId: e.target.value })
                }
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          )}
          <div className="w-48">
            <select
              value={filters.step}
              onChange={(e) => setFilters({ ...filters, step: e.target.value })}
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">All Steps</option>
              <option value="1">Step 1</option>
              <option value="2">Step 2</option>
              <option value="3">Step 3</option>
            </select>
          </div>
        </div>
      </div>

      {/* Kanban Board */}
      <div className="flex gap-4 overflow-x-auto pb-4">
        {renderedStatuses.map((statusKey) => renderColumn(statusKey))}
      </div>

      {/* Scroll sentinel for infinite scroll (Leads tab only) */}
      {!isBoard && hasMoreLeads && (
        <div ref={scrollSentinelRef} className="flex justify-center py-4">
          {loadingMore ? (
            <span className="text-sm text-gray-500">Loading more...</span>
          ) : (
            <span className="text-sm text-gray-400">Scroll for more</span>
          )}
        </div>
      )}

      {/* Confirmation Modal */}
      {showConfirmation && pendingStatusChange && (
        <ConfirmationModal
          title="Confirm Status Change"
          message={
            <div className="space-y-2">
              {pendingStatusChange.group ? (
                <>
                  <p>
                    Move{' '}
                    <strong>
                      {pendingStatusChange.quoteIds.length} quote
                      {pendingStatusChange.quoteIds.length === 1 ? '' : 's'}
                    </strong>{' '}
                    to a new status?
                  </p>
                  <div className="mt-4 p-3 bg-gray-50 rounded-lg text-sm space-y-2">
                    {pendingStatusChange.group.linkedOrderNumber && (
                      <div>
                        <span className="font-medium">Order:</span>{' '}
                        {pendingStatusChange.group.linkedOrderNumber}
                      </div>
                    )}
                    <div>
                      <span className="font-medium">From:</span>{' '}
                      <span className="font-semibold text-blue-600">
                        {formatStatusLabel(pendingStatusChange.fromStatus)}
                      </span>
                    </div>
                    <div>
                      <span className="font-medium">To:</span>{' '}
                      <span className="font-semibold text-green-600">
                        {formatStatusLabel(pendingStatusChange.toStatus)}
                      </span>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <p>Are you sure you want to change the status of this quote?</p>
                  <div className="mt-4 p-3 bg-gray-50 rounded-lg text-sm space-y-2">
                    <div>
                      <span className="font-medium">Quote:</span>{' '}
                      {pendingStatusChange.quote.contact?.name}
                    </div>
                    <div>
                      <span className="font-medium">From:</span>{' '}
                      <span className="font-semibold text-blue-600">
                        {formatStatusLabel(pendingStatusChange.fromStatus)}
                      </span>
                    </div>
                    <div>
                      <span className="font-medium">To:</span>{' '}
                      <span className="font-semibold text-green-600">
                        {formatStatusLabel(pendingStatusChange.toStatus)}
                      </span>
                    </div>
                  </div>
                </>
              )}
            </div>
          }
          onConfirm={confirmStatusChange}
          onCancel={cancelStatusChange}
        />
      )}

      {/* Shipment Modal (DELIVERING transition) */}
      {showShipmentModal && pendingStatusChange && (
        pendingStatusChange.group ? (
          <ShipmentModal
            group={pendingStatusChange.group}
            subsetQuotes={
              pendingStatusChange.group.quotesByStatus?.[pendingStatusChange.fromStatus] ||
              []
            }
            preselectedQuoteIds={pendingStatusChange.quoteIds}
            onConfirm={confirmShipmentChange}
            onCancel={() => {
              setShowShipmentModal(false);
              setPendingStatusChange(null);
            }}
          />
        ) : (
          <ShipmentModal
            quote={pendingStatusChange.quote}
            onConfirm={confirmShipmentChange}
            onCancel={() => {
              setShowShipmentModal(false);
              setPendingStatusChange(null);
            }}
          />
        )
      )}

      {/* Internal Notes Modal */}
      {showNotesModal && selectedQuote && (
        <InternalNotesModal
          quote={selectedQuote}
          onSave={handleSaveNotes}
          onCancel={() => {
            setShowNotesModal(false);
            setSelectedQuote(null);
          }}
        />
      )}

      {/* Packing Guide Drawer */}
      <PackingGuideDrawer
        open={!!packingGuideQuoteId}
        onClose={() => setPackingGuideQuoteId(null)}
        quoteId={packingGuideQuoteId}
      />
    </div>
  );
}

export default SlicerQuotes;
